<script setup>
import { Link, router } from '@inertiajs/vue3';
import { computed, ref, getCurrentInstance } from 'vue';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import Tag from 'primevue/tag';
import ConfirmDialog from 'primevue/confirmdialog';
import { useConfirm } from 'primevue/useconfirm';
import { useToast } from 'primevue/usetoast';
import AdminMainLayout from '@/layout/admin/AdminMainLayout.vue';
import { useLoading } from '@/composables/useLoading';

const { proxy } = getCurrentInstance();
const $t = proxy.$t;
const confirm = useConfirm();
const toast = useToast();
const { showLoading, hideLoading } = useLoading();

const props = defineProps({
    title: String,
    languages: Object,
});


const languagesData = computed(() => {
    return props.languages.data || [];
});


const destroy = (language) => {
    confirm.require({
        message: `${$t('Are you sure you want to delete')} ${language.name}?`,
        header: $t('Delete Confirmation'),
        icon: 'pi pi-exclamation-triangle',
        rejectClass: 'p-button-secondary p-button-outlined',
        acceptClass: 'p-button-danger',
        accept: () => {
            router.delete(route('languages.destroy', language.id), {
                preserveScroll: true,
                preserveState: true,
                onStart: () => showLoading('Deleting language...'),
                onFinish: () => hideLoading(),
                onSuccess: () => {
                    toast.add({ severity: 'success', summary: 'Success', detail: 'Language deleted successfully', life: 3000 });
                },
                onError: () => {
                    toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to delete language', life: 3000 });
                }
            });
        }
    });
};

const onPage = (event) => {
    const page = event.page + 1;
    router.get(route('languages.index'), { page }, { 
        preserveState: true, 
        preserveScroll: true,
        onStart: () => showLoading('Loading page...'),
        onFinish: () => hideLoading()
    });
};

const paginatorTemplate = {
    layout: 'FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport',
    CurrentPageReport: 'Showing {first} to {last} of {totalRecords} entries'
};
</script>

<template>
    <AdminMainLayout>
        <ConfirmDialog />
        
        <div class="flex justify-between items-center mb-4">
            <h2 class="text-2xl font-semibold text-gray-900">{{ $t('Languages') }}</h2>
            <Button :label="$t('Add Language')" icon="pi pi-plus" class="p-button-success" 
                @click="() => router.visit(route('languages.create'), {
                    onStart: () => showLoading('Loading create form...'),
                    onFinish: () => hideLoading()
                })" />
        </div>

        <DataTable 
            :value="languagesData" 
            :paginator="true" 
            :rows="15"
            :totalRecords="languages.total"
            :lazy="true"
            @page="onPage"
            dataKey="id"
            responsiveLayout="scroll"
            :paginatorTemplate="paginatorTemplate.layout"
            currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
            class="p-datatable-sm">
            

            <template #empty>
                <div class="text-center py-4">{{ $t('No languages found.') }}</div>
            </template>

            <Column field="name" :header="$t('Name')" :sortable="true" style="min-width: 200px">
                <template #body="slotProps">
                    <span class="text-blue-600 hover:text-blue-800 font-medium cursor-pointer"
                        @click="() => router.visit(route('languages.edit', slotProps.data.id), {
                            onStart: () => showLoading('Loading language...'),
                            onFinish: () => hideLoading()
                        })">
                        {{ slotProps.data.name }}
                    </span>
                </template>
            </Column>

            <Column field="sites_count" :header="$t('Sites')" :sortable="true" style="min-width: 100px">
                <template #body="slotProps">
                    <Tag :value="slotProps.data.sites_count || 0" severity="info" />
                </template>
            </Column>

            <Column :header="$t('Actions')" :exportable="false" style="min-width: 150px">
                <template #body="slotProps">
                    <div class="flex gap-2">
                        <Button icon="pi pi-pencil" class="p-button-rounded p-button-text p-button-info" 
                            v-tooltip.top="$t('Edit')"
                            @click="() => router.visit(route('languages.edit', slotProps.data.id), {
                                onStart: () => showLoading('Loading language...'),
                                onFinish: () => hideLoading()
                            })" />
                        <Button icon="pi pi-trash" class="p-button-rounded p-button-text p-button-danger" 
                            @click="destroy(slotProps.data)" v-tooltip.top="$t('Delete')" />
                    </div>
                </template>
            </Column>
        </DataTable>
    </AdminMainLayout>
</template>

<style scoped>
.card {
    background: white;
    padding: 1.5rem;
    border-radius: 0.5rem;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
}
</style>